import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

class PDFGenerator {
  constructor() {
    this.doc = null;
    this.pageWidth = 210; // A4 width in mm
    this.pageHeight = 297; // A4 height in mm
    this.margin = 20;
    this.currentY = this.margin;
    this.logoHeight = null; // Hauteur du logo pour ajuster l'espacement
  }

  // Pagination: Page X/Y en bas à droite
  addPageNumbers() {
    const doc = this.doc;
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Page ${i}/${pageCount}`, this.pageWidth - this.margin, this.pageHeight - 8, { align: 'right' });
    }
  }

  // Génération PDF pour les devis
  async generateQuotePDF(quote, customer, vehicle, settings) {
    this.doc = new jsPDF();
    this.currentY = this.margin;
    this.logoHeight = null; // Réinitialiser la hauteur du logo

    try {
      // En-tête avec logo
      await this.addHeader(settings, 'DEVIS');
      
      // Informations du devis
      this.addQuoteInfo(quote, customer, vehicle);
      
      // Tableau des prestations
      this.addItemsTable(quote.items || []);
      
      // Totaux
      this.addTotals(quote);
      
      // Pied de page
      this.addFooter(settings, 'quote');
      // Pagination
      this.addPageNumbers();
      return this.doc;
    } catch (error) {
      console.error('Erreur génération PDF devis:', error);
      throw error;
    }
  }

  // Génération PDF pour les factures
  async generateInvoicePDF(invoice, customer, vehicle, settings) {
    this.doc = new jsPDF();
    this.currentY = this.margin;
    this.logoHeight = null; // Réinitialiser la hauteur du logo

    try {
      // En-tête avec logo
      await this.addHeader(settings, 'FACTURE');
      
      // Informations de la facture
      this.addInvoiceInfo(invoice, customer, vehicle);
      
      // Tableau des prestations
      this.addItemsTable(invoice.items || []);
      
      // Totaux
      this.addTotals(invoice);
      
      // Informations de paiement
      this.addPaymentInfo(invoice, settings);
      
      // Pied de page
      this.addFooter(settings, 'invoice');
      // Pagination
      this.addPageNumbers();
      return this.doc;
    } catch (error) {
      console.error('Erreur génération PDF facture:', error);
      throw error;
    }
  }

  // Ajouter l'en-tête avec logo
  async addHeader(settings, documentType) {
    const doc = this.doc;
    
    // Logo de l'entreprise (si disponible)
    if (settings?.logo) {
      try {
        const logoUrl = `https://christophedepanne.infocolab.online/api/files/settings/${settings.id}/${settings.logo}`;
        const logoImg = await this.loadImage(logoUrl);
        
        // Calculer les dimensions du logo en gardant les proportions
        const logoDimensions = this.calculateLogoDimensions(logoImg, 50, 30); // max 50mm de large, 30mm de haut
        
        // Détecter le format de l'image
        const imageFormat = this.detectImageFormat(settings.logo);
        
        doc.addImage(
          logoImg, 
          imageFormat, 
          this.margin, 
          this.currentY, 
          logoDimensions.width, 
          logoDimensions.height
        );
        
        // Ajuster l'espacement selon la hauteur du logo
        this.logoHeight = logoDimensions.height;
      } catch (error) {
        console.warn('Impossible de charger le logo:', error);
      }
    }

    // Deux colonnes d'en-tête
    const leftX = this.margin + 55; // à droite du bloc logo (50mm) + marge de 5mm
    const rightX = this.pageWidth - this.margin;
    
    // Colonne gauche: infos société (à côté du logo)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const companyName = settings?.company_name || 'Christophe Dépanne';
    doc.text(companyName, leftX, this.currentY + 5, { align: 'left' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yLeft = this.currentY + 12;
    if (settings?.company_address) {
      doc.text(settings.company_address, leftX, yLeft, { align: 'left' });
      yLeft += 5;
    }
    if (settings?.company_postal_code && settings?.company_city) {
      doc.text(`${settings.company_postal_code} ${settings.company_city}`, leftX, yLeft, { align: 'left' });
      yLeft += 5;
    }
    if (settings?.company_phone) {
      doc.text(`Tél: ${settings.company_phone}`, leftX, yLeft, { align: 'left' });
      yLeft += 5;
    }
    if (settings?.company_email) {
      doc.text(`Email: ${settings.company_email}`, leftX, yLeft, { align: 'left' });
      yLeft += 5;
    }

    // Colonne droite: infos légales (SIRET, TVA, RCS)
    let yRight = this.currentY + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Informations légales', rightX, yRight, { align: 'right' });
    yRight += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (settings?.siret) {
      doc.text(`SIRET: ${settings.siret}`, rightX, yRight, { align: 'right' });
      yRight += 5;
    }
    if (settings?.tva_number) {
      doc.text(`N° TVA: ${settings.tva_number}`, rightX, yRight, { align: 'right' });
      yRight += 5;
    }
    if (settings?.rcs) {
      doc.text(`RCS: ${settings.rcs}`, rightX, yRight, { align: 'right' });
      yRight += 5;
    }

    // Titre du document - ajuster l'espacement selon la hauteur du logo
    const logoSpacing = this.logoHeight ? Math.max(this.logoHeight + 10, 40) : 40;
    this.currentY += logoSpacing;
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 82, 204); // Bleu
    doc.text(documentType, this.pageWidth / 2, this.currentY, { align: 'center' });
    
    this.currentY += 15;
    doc.setTextColor(0, 0, 0); // Retour au noir
  }

  // Informations du devis
  addQuoteInfo(quote, customer, vehicle) {
    const doc = this.doc;
    
    // Informations client (gauche)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT:', this.margin, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosClient = this.currentY + 7;
    
    doc.text(customer?.name || 'Client inconnu', this.margin, yPosClient);
    yPosClient += 5;
    
    if (customer?.company) {
      doc.text(customer.company, this.margin, yPosClient);
      yPosClient += 5;
    }
    
    if (customer?.address) {
      const raw = String(customer.address || '').trim();
      const postalMatches = [...raw.matchAll(/\b\d{5}\b/g)];
      let line1 = '';
      let line2 = '';
      if (postalMatches.length > 0) {
        const last = postalMatches[postalMatches.length - 1];
        const idx = last.index || 0;
        line1 = raw.slice(0, idx).replace(/[\n,]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
        line2 = raw.slice(idx).replace(/[\n,]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
      } else {
        const parts = raw.split(/\n|,/).map(s => s.trim()).filter(Boolean);
        if (parts.length >= 2) {
          // Chercher un segment qui ressemble à CP+ville
          const cpIdx = parts.findIndex(p => /\b\d{5}\b/.test(p));
          if (cpIdx >= 0) {
            line1 = parts.slice(0, cpIdx).join(', ') || parts[0] || '';
            line2 = parts.slice(cpIdx).join(', ');
          } else {
            line1 = parts[0];
            line2 = parts.slice(1).join(', ');
          }
        } else {
          const wrap = doc.splitTextToSize(raw, 80);
          line1 = wrap[0] || '';
          line2 = wrap[1] || '';
        }
      }
      if (line1) { doc.text(line1, this.margin, yPosClient); yPosClient += 5; }
      if (line2) { doc.text(line2, this.margin, yPosClient); yPosClient += 5; }
    }
    
    if (customer?.phone) {
      doc.text(`Tél: ${customer.phone}`, this.margin, yPosClient);
      yPosClient += 5;
    }
    
    if (customer?.email) {
      doc.text(`Email: ${customer.email}`, this.margin, yPosClient);
      yPosClient += 5;
    }

    // Informations du devis (droite)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DEVIS:', this.pageWidth - this.margin - 60, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosQuote = this.currentY + 7;
    
    doc.text(`N° ${quote.quote_number}`, this.pageWidth - this.margin - 60, yPosQuote);
    yPosQuote += 5;
    
    doc.text(`Date: ${this.formatDate(quote.created)}`, this.pageWidth - this.margin - 60, yPosQuote);
    yPosQuote += 5;
    
    if (quote.valid_until) {
      doc.text(`Valide jusqu'au: ${this.formatDate(quote.valid_until)}`, this.pageWidth - this.margin - 60, yPosQuote);
      yPosQuote += 5;
    }

    // Informations du véhicule (colonne gauche) et Notes (colonne droite)
    const topY = this.currentY + 50; // point de départ commun sous les blocs précédents
    this.currentY = topY;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('VÉHICULE:', this.margin, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosVehicle = this.currentY + 7;
    
    const vehicleInfo = `${vehicle?.brand || ''} ${vehicle?.model || ''} ${vehicle?.year ? `(${vehicle.year})` : ''}`.trim();
    doc.text(vehicleInfo, this.margin, yPosVehicle);
    
    if (vehicle?.license_plate) {
      yPosVehicle += 5;
      doc.text(`Immatriculation: ${vehicle.license_plate}`, this.margin, yPosVehicle);

      // Kilométrage juste en dessous, même style
      if (vehicle?.mileage !== undefined && vehicle?.mileage !== null && vehicle?.mileage !== '') {
        yPosVehicle += 5;
        const raw = String(vehicle.mileage).replace(/\s+/g, '');
        const digits = /^(\d+)$/.test(raw) ? RegExp.$1 : String(parseInt(raw, 10) || '0');
        const kmText = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        doc.text(`Kilométrage: ${kmText} km`, this.margin, yPosVehicle);
      }
    }

    const leftEndY = yPosVehicle;

    // Colonne droite: NOTES du devis (si présentes)
    let rightEndY = topY;
    if (quote?.notes) {
      const rightWidth = 80; // mm
      const rightX = this.pageWidth - this.margin - rightWidth;
      // Titre
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('NOTES :', rightX, topY);
      // Contenu
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const clean = String(quote.notes).replace(/<[^>]*>/g, '');
      const lines = doc.splitTextToSize(clean, rightWidth);
      let noteY = topY + 6;
      lines.forEach(line => {
        doc.text(line, rightX, noteY);
        noteY += 5;
      });
      rightEndY = noteY;
    }

    // Aligner la suite sous le bloc le plus long
    this.currentY = Math.max(leftEndY, rightEndY) + 18;
  }

  // Informations de la facture
  addInvoiceInfo(invoice, customer, vehicle) {
    const doc = this.doc;
    
    // Informations client (gauche)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT:', this.margin, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosClientInv = this.currentY + 7;
    
    doc.text(customer?.name || 'Client inconnu', this.margin, yPosClientInv);
    yPosClientInv += 5;
    
    if (customer?.company) {
      doc.text(customer.company, this.margin, yPosClientInv);
      yPosClientInv += 5;
    }
    
    if (customer?.address) {
      doc.text(customer.address, this.margin, yPosClientInv);
      yPosClientInv += 5;
    }
    
    if (customer?.phone) {
      doc.text(`Tél: ${customer.phone}`, this.margin, yPosClientInv);
      yPosClientInv += 5;
    }
    
    if (customer?.email) {
      doc.text(`Email: ${customer.email}`, this.margin, yPosClientInv);
      yPosClientInv += 5;
    }

    // Informations de la facture (droite)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURE:', this.pageWidth - this.margin - 60, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosInvoiceMeta = this.currentY + 7;
    
    doc.text(`N° ${invoice.invoice_number}`, this.pageWidth - this.margin - 60, yPosInvoiceMeta);
    yPosInvoiceMeta += 5;
    
    doc.text(`Date: ${this.formatDate(invoice.created)}`, this.pageWidth - this.margin - 60, yPosInvoiceMeta);
    yPosInvoiceMeta += 5;
    
    if (invoice.due_date) {
      doc.text(`Échéance: ${this.formatDate(invoice.due_date)}`, this.pageWidth - this.margin - 60, yPosInvoiceMeta);
      yPosInvoiceMeta += 5;
    }

    // Informations du véhicule (colonne gauche) et Notes (colonne droite)
    const topY = this.currentY + 50;
    this.currentY = topY;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('VÉHICULE:', this.margin, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosVehicleInv = this.currentY + 7;
    
    const vehicleInfo = `${vehicle?.brand || ''} ${vehicle?.model || ''} ${vehicle?.year ? `(${vehicle.year})` : ''}`.trim();
    doc.text(vehicleInfo, this.margin, yPosVehicleInv);
    
    if (vehicle?.license_plate) {
      yPosVehicleInv += 5;
      doc.text(`Immatriculation: ${vehicle.license_plate}`, this.margin, yPosVehicleInv);
      // Kilométrage sous l'immatriculation (même style)
      if (vehicle?.mileage !== undefined && vehicle?.mileage !== null && vehicle?.mileage !== '') {
        yPosVehicleInv += 5;
        const raw = String(vehicle.mileage).replace(/\s+/g, '');
        const digits = /^(\d+)$/.test(raw) ? RegExp.$1 : String(parseInt(raw, 10) || '0');
        const kmText = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        doc.text(`Kilométrage: ${kmText} km`, this.margin, yPosVehicleInv);
      }
    }
    const leftEndY2 = yPosVehicleInv;

    // Colonne droite: NOTES de facture (si présentes)
    let rightEndY2 = topY;
    if (invoice?.notes) {
      const rightWidth = 80; // mm
      const rightX = this.pageWidth - this.margin - rightWidth;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('NOTES :', rightX, topY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const clean = String(invoice.notes).replace(/<[^>]*>/g, '');
      const lines = doc.splitTextToSize(clean, rightWidth);
      let noteY = topY + 6;
      lines.forEach(line => {
        doc.text(line, rightX, noteY);
        noteY += 5;
      });
      rightEndY2 = noteY;
    }

    this.currentY = Math.max(leftEndY2, rightEndY2) + 18;
  }

  // Tableau des prestations
  addItemsTable(items) {
    const doc = this.doc;
    
    // En-tête du tableau
    doc.setFillColor(240, 240, 240);
    doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 10, 'F');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Description', this.margin + 2, this.currentY + 6);
    doc.text('Qté', this.pageWidth - 80, this.currentY + 6);
    doc.text('Prix unit. HT', this.pageWidth - 60, this.currentY + 6);
    doc.text('Total', this.pageWidth - this.margin - 2, this.currentY + 6, { align: 'right' });
    
    this.currentY += 12;
    
    // Lignes du tableau
    doc.setFont('helvetica', 'normal');
    const descStartX = this.margin + 2;
    const descRightLimit = this.pageWidth - 95; // laisse la place pour Qté/PU/Total
    const descWidth = Math.max(40, descRightLimit - descStartX);
    items.forEach((item) => {
      const description = item.description || '';
      const descLines = doc.splitTextToSize(description, descWidth);
      const rowHeight = Math.max(10, 5 + (descLines.length - 1) * 5 + 5); // top padding 5, each extra line +5, bottom baseline

      // Saut de page si la ligne ne tient pas avec réserve pied de page
      if (this.currentY + rowHeight > this.pageHeight - 80) {
        doc.addPage();
        this.currentY = this.margin;
        // Réafficher entête du tableau
        doc.setFillColor(240, 240, 240);
        doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 10, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Description', this.margin + 2, this.currentY + 6);
        doc.text('Qté', this.pageWidth - 80, this.currentY + 6);
        doc.text('Prix unit. HT', this.pageWidth - 60, this.currentY + 6);
        doc.text('Total', this.pageWidth - this.margin - 2, this.currentY + 6, { align: 'right' });
        this.currentY += 12;
        doc.setFont('helvetica', 'normal');
      }

      // Rendu description multi-lignes
      let lineY = this.currentY + 5;
      descLines.forEach((l) => {
        doc.text(l, descStartX, lineY);
        lineY += 5;
      });

      const total = (item.quantity || 0) * (item.unit_price || 0);
      const baselineY = this.currentY + 5; // aligné sur la première ligne
      doc.text(String(item.quantity || 0), this.pageWidth - 80, baselineY);
      doc.text(`${(item.unit_price || 0).toFixed(2)}€`, this.pageWidth - 60, baselineY);
      doc.text(`${total.toFixed(2)}€`, this.pageWidth - this.margin - 2, baselineY, { align: 'right' });

      // Ligne de séparation
      doc.setDrawColor(200, 200, 200);
      doc.line(this.margin, this.currentY + rowHeight - 2, this.pageWidth - this.margin, this.currentY + rowHeight - 2);

      this.currentY += rowHeight;
    });
    
    this.currentY += 5;
  }

  // Totaux
  addTotals(document) {
    const doc = this.doc;
    
    // Saut de page si l’espace restant est insuffisant pour les totaux + pied de page
    const neededForTotals = 35; // env. 3 lignes + marges
    const footerReserve = 40; // réserve pour éviter le chevauchement avec le pied de page
    if (this.currentY > this.pageHeight - (neededForTotals + footerReserve)) {
      doc.addPage();
      this.currentY = this.margin;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const startX = this.pageWidth - 80;
    
    // Sous-total HT
    doc.text('Sous-total HT:', startX, this.currentY);
    doc.text(`${(document.subtotal || 0).toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
    this.currentY += 7;
    
    // Remise (si présente)
    const hasDiscount = typeof document.discount_total === 'number' && document.discount_total > 0;
    if (hasDiscount) {
      // 1) Intitulé (optionnel) collé juste avant "Remise"
      if (document.discount_label) {
        const pad = 2; // petit espace entre l'intitulé et "Remise"
        let core = String(document.discount_label);
        let composed = core + ' - ';
        // Tronquer pour que le texte tienne avant startX en gardant le pad
        while (doc.getTextWidth(composed) > ((startX - this.margin) - pad) && core.length > 1) {
          core = core.slice(0, -1);
          composed = core + '… - ';
        }
        const labelX = startX - doc.getTextWidth(composed) - pad;
        // Intitulé en gras
        doc.setFont('helvetica', 'bold');
        doc.text(composed, labelX, this.currentY);
        // Retour à la police normale pour le reste de la ligne
        doc.setFont('helvetica', 'normal');
      }
      // 2) "Remise (X%):" aligné comme TVA/Total (position fixe)
      const baseLabel = `Remise${(document.discount_type === 'percent' && typeof document.discount_value === 'number') ? ` (${document.discount_value}%)` : ''}:`;
      doc.text(baseLabel, startX, this.currentY);
      // 3) Montant à droite (placement fixe) avec signe négatif
      doc.text(`-${(document.discount_total || 0).toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
      this.currentY += 7;
    }
    
    // TVA
    if (document.tax_enabled === false) {
      doc.text(`TVA non applicable`, startX, this.currentY);
      // Pas de montant affiché sur la droite quand non applicable
      this.currentY += 7;
    } else {
      doc.text(`TVA (${document.tax_rate || 20}%):`, startX, this.currentY);
      doc.text(`${(document.tax_amount || 0).toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
      this.currentY += 7;
    }
    
    // Total TTC
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total TTC:', startX, this.currentY);
    doc.text(`${(document.total || 0).toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
    
    this.currentY += 15;
  }

  // Informations de paiement (factures uniquement)
  addPaymentInfo(invoice, settings) {
    const doc = this.doc;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('MODALITÉS DE PAIEMENT:', this.margin, this.currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    this.currentY += 8;
    
    const paymentTerms = settings?.invoice_payment_terms || 30;
    doc.text(`Paiement à ${paymentTerms} jours`, this.margin, this.currentY);
    this.currentY += 5;
    
    if (settings?.bank_name && settings?.bank_iban) {
      doc.text(`Banque: ${settings.bank_name}`, this.margin, this.currentY);
      this.currentY += 5;
      doc.text(`IBAN: ${settings.bank_iban}`, this.margin, this.currentY);
      this.currentY += 5;
      
      if (settings.bank_bic) {
        doc.text(`BIC: ${settings.bank_bic}`, this.margin, this.currentY);
        this.currentY += 5;
      }
    }
    
    this.currentY += 10;
  }

  // Pied de page
  addFooter(settings, documentType) {
    const doc = this.doc;
    
    // Footer personnalisé (centré)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const centerX = this.pageWidth / 2;
    let footerY = this.pageHeight - 30;

    const footerText = documentType === 'quote' ? settings?.quote_footer : settings?.invoice_footer;
    if (footerText) {
      const lines = doc.splitTextToSize(footerText.replace(/<[^>]*>/g, ''), this.pageWidth - 2 * this.margin);
      // Render each line centered
      let y = footerY + 5;
      lines.forEach(line => {
        doc.text(line, centerX, y, { align: 'center' });
        y += 4.5;
      });
    }
  }

  // Rendu de notes libres avec wrap et gestion de page
  addDocumentNotes(notes) {
    const doc = this.doc;
    if (!notes) return;

    // Saut de page si proche du bas
    if (this.currentY > this.pageHeight - 50) {
      doc.addPage();
      this.currentY = this.margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('NOTES :', this.margin, this.currentY);
    this.currentY += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const clean = String(notes).replace(/<[^>]*>/g, '');
    const lines = doc.splitTextToSize(clean, this.pageWidth - 2 * this.margin);

    lines.forEach(line => {
      if (this.currentY > this.pageHeight - 20) {
        doc.addPage();
        this.currentY = this.margin;
      }
      doc.text(line, this.margin, this.currentY);
      this.currentY += 5;
    });

    this.currentY += 5;
  }

  // Utilitaires
  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  }

  loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  // Calculer les dimensions du logo en gardant les proportions
  calculateLogoDimensions(img, maxWidth, maxHeight) {
    const originalWidth = img.naturalWidth || img.width;
    const originalHeight = img.naturalHeight || img.height;
    
    if (!originalWidth || !originalHeight) {
      // Si on ne peut pas obtenir les dimensions, utiliser des valeurs par défaut
      return { width: maxWidth * 0.8, height: maxHeight * 0.8 };
    }
    
    // Calculer le ratio d'aspect
    const aspectRatio = originalWidth / originalHeight;
    
    let newWidth = maxWidth;
    let newHeight = maxWidth / aspectRatio;
    
    // Si la hauteur dépasse le maximum, ajuster par la hauteur
    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = maxHeight * aspectRatio;
    }
    
    return {
      width: newWidth,
      height: newHeight
    };
  }

  // Détecter le format d'image à partir de l'extension
  detectImageFormat(filename) {
    if (!filename) return 'JPEG';
    
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'png':
        return 'PNG';
      case 'jpg':
      case 'jpeg':
        return 'JPEG';
      case 'gif':
        return 'GIF';
      case 'webp':
        return 'WEBP';
      default:
        return 'JPEG'; // Par défaut
    }
  }

  // Télécharger le PDF
  download(filename) {
    if (this.doc) {
      this.doc.save(filename);
    }
  }

  // Aperçu du PDF
  preview() {
    if (this.doc) {
      const pdfBlob = this.doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    }
  }

  // Obtenir le PDF comme blob pour le partage
  getBlob() {
    if (this.doc) {
      return this.doc.output('blob');
    }
    return null;
  }
}

export default PDFGenerator;
